import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BrandedPreviewIframe } from '@/components/email/BrandedPreviewIframe';
import { wrapInTemplate, extractVimeoId } from '@/lib/emailTemplate';
import { uploadEmailImage, isSupportedImage } from '@/lib/emailImages';
import {
  Eye,
  Code,
  ImagePlus,
  Loader2,
  Megaphone,
  PenLine,
  Plus,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

function interpolateTemplateText(text, payload) {
  return String(text).replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = payload?.[key];
    return typeof value === 'string' && value.trim() ? value.trim() : '';
  });
}

function resolveHeroImageUrl(imageUrl, previewPayload) {
  const trimmed = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  if (!trimmed) return null;
  const resolved = interpolateTemplateText(trimmed, previewPayload);
  if (!resolved || !/^https?:\/\//i.test(resolved)) return null;
  return resolved;
}

function buildHeroObject({ enabled, headline, accentWord, tagline, imageUrl }) {
  if (!enabled) return null;
  const hasHeadline = Boolean(headline?.trim());
  const hasImage = Boolean(imageUrl?.trim());
  if (!hasHeadline && !hasImage) return null;
  return {
    headline: hasHeadline ? headline.trim() : undefined,
    accentWord: accentWord?.trim() || undefined,
    tagline: tagline?.trim() || undefined,
    imageUrl: hasImage ? imageUrl.trim() : undefined,
  };
}

export function EmailRichBodyEditor({
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  language,
  onLanguageChange,
  heroEnabled,
  onHeroEnabledChange,
  heroHeadline,
  onHeroHeadlineChange,
  heroAccentWord,
  onHeroAccentWordChange,
  heroTagline,
  onHeroTaglineChange,
  heroImageUrl,
  onHeroImageUrlChange,
  videos,
  onVideosChange,
  previewPayload = {},
  previewRecipientName = '',
  showLanguage = true,
  heroImageOnly = false,
}) {
  const [viewMode, setViewMode] = useState('branded');
  const [videoInput, setVideoInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [heroImageError, setHeroImageError] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) {
      onBodyChange(body ? body + text : text);
      return;
    }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    onBodyChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleImageFile = async (file) => {
    if (!isSupportedImage(file)) {
      toast.error('Only PNG, JPEG, GIF or WebP images are supported');
      return;
    }
    setUploading(true);
    setViewMode('edit');
    try {
      const { url } = await uploadEmailImage(file);
      const figure = `\n<figure style="margin:24px 0;text-align:center;">\n  <img src="${url}" alt="" style="display:block;max-width:100%;height:auto;margin:0 auto;"/>\n</figure>\n`;
      insertAtCursor(figure);
      toast.success('Image inserted');
    } catch (err) {
      toast.error(`Image upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleBodyPaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) handleImageFile(file);
        return;
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) handleImageFile(file);
    event.target.value = '';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleBodyDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) handleImageFile(file);
  };

  const addVideo = () => {
    const url = videoInput.trim();
    if (!url) return;
    if (!extractVimeoId(url)) {
      toast.error('Paste a valid Vimeo URL');
      return;
    }
    if (videos.includes(url)) {
      toast.error('Video already added');
      return;
    }
    onVideosChange([...videos, url]);
    setVideoInput('');
  };

  const buildPreviewHero = () => {
    const hero = buildHeroObject({
      enabled: heroEnabled,
      headline: heroHeadline,
      accentWord: heroAccentWord,
      tagline: heroTagline,
      imageUrl: heroImageUrl,
    });
    if (!hero) return null;
    return {
      headline: hero.headline
        ? interpolateTemplateText(hero.headline, previewPayload)
        : undefined,
      accentWord: hero.accentWord
        ? interpolateTemplateText(hero.accentWord, previewPayload)
        : undefined,
      tagline: hero.tagline
        ? interpolateTemplateText(hero.tagline, previewPayload)
        : undefined,
      imageUrl: resolveHeroImageUrl(hero.imageUrl, previewPayload) ?? undefined,
    };
  };

  const resolvedHeroImageUrl = resolveHeroImageUrl(heroImageUrl, previewPayload);

  const rawHtml = body.includes('<') ? body : `<p>${body.replace(/\n/g, '<br/>')}</p>`;
  const previewHtml = wrapInTemplate(interpolateTemplateText(rawHtml, previewPayload), {
    hero: buildPreviewHero(),
    videos,
    recipientName: previewRecipientName,
    language,
  });

  const renderBodyContent = () => {
    if (viewMode === 'edit') {
      return (
        <Textarea
          ref={textareaRef}
          placeholder="Write HTML body — paste screenshots, embed images, use {{programName}} placeholders."
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          onPaste={handleBodyPaste}
          rows={12}
          className="font-mono text-sm"
        />
      );
    }

    if (viewMode === 'preview') {
      return (
        <div
          className="min-h-[200px] p-4 border rounded-md bg-white dark:bg-zinc-950 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{
            __html: interpolateTemplateText(rawHtml, previewPayload),
          }}
        />
      );
    }

    return (
      <BrandedPreviewIframe html={previewHtml} title="Template branded preview" />
    );
  };

  return (
    <div className="space-y-4">
      {showLanguage && (
        <div className="space-y-2">
          <Label>Language</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={language === 'de' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onLanguageChange('de')}
            >
              Deutsch
            </Button>
            <Button
              type="button"
              variant={language === 'en' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onLanguageChange('en')}
            >
              English
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="template-subject">Subject</Label>
        <Input
          id="template-subject"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          placeholder="Willkommen — {{programName}} ist jetzt aktiv"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Label>Body</Label>
          <div className="flex items-center gap-1 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="h-7 px-2 text-xs"
            >
              {uploading ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <ImagePlus className="h-3 w-3 mr-1" />
              )}
              Insert image
            </Button>
            <div className="w-px h-5 bg-border mx-1" aria-hidden />
            <Button
              type="button"
              variant={viewMode === 'edit' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('edit')}
              className="h-7 px-2 text-xs"
            >
              <PenLine className="h-3 w-3 mr-1" />
              Edit
            </Button>
                <Button
                  type="button"
                  variant={viewMode === 'preview' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('preview')}
                  className="h-7 px-2 text-xs"
                >
                  <Code className="h-3 w-3 mr-1" />
                  Body only
                </Button>
                <Button
                  type="button"
                  variant={viewMode === 'branded' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('branded')}
                  className="h-7 px-2 text-xs"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Full email
                </Button>
          </div>
        </div>
        <div
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleBodyDrop}
          className={`relative rounded-md transition-all ${
            dragOver ? 'ring-2 ring-lime-500 ring-offset-2 ring-offset-background' : ''
          }`}
        >
          {renderBodyContent()}
          {dragOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-lime-500/10 border-2 border-dashed border-lime-500 rounded-md pointer-events-none z-10">
              <div className="flex items-center gap-2 px-4 py-2 bg-background border rounded-full shadow-sm text-sm font-medium">
                <Upload className="h-4 w-4" />
                Drop image to embed
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3 p-4 border rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Megaphone className="h-4 w-4" />
            Hero block
          </div>
          <Button
            type="button"
            variant={heroEnabled ? 'default' : 'outline'}
            size="sm"
            onClick={() => onHeroEnabledChange(!heroEnabled)}
            className="h-7 px-3 text-xs"
          >
            {heroEnabled ? 'Enabled' : 'Add hero'}
          </Button>
        </div>
        {heroEnabled && (
          <div className="space-y-3 pt-2">
            {!heroImageOnly && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Headline</Label>
                  <Input
                    placeholder="{{programName}}"
                    value={heroHeadline}
                    onChange={(e) => onHeroHeadlineChange(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Accent word</Label>
                    <Input
                      value={heroAccentWord}
                      onChange={(e) => onHeroAccentWordChange(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tagline</Label>
                    <Input
                      value={heroTagline}
                      onChange={(e) => onHeroTaglineChange(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">{heroImageOnly ? 'Hero photo URL' : 'Hero image URL'}</Label>
              <Input
                placeholder="{{programImageUrl}} or https://…/image.jpg"
                value={heroImageUrl}
                onChange={(e) => {
                  setHeroImageError(false);
                  onHeroImageUrlChange(e.target.value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                {heroImageOnly
                  ? 'Full-width photo only — no program title or lime frame. Use Full email view to preview.'
                  : 'Shown inside the lime hero card above the body — use Full email view and scroll to the top of the preview. Click Save draft so test send and Publish keep this URL.'}
              </p>
              {resolvedHeroImageUrl ? (
                <div className="mt-2 rounded-lg border bg-muted/30 p-2">
                  <p className="text-xs text-muted-foreground mb-2">Hero image check</p>
                  {heroImageError ? (
                    <p className="text-xs text-destructive">
                      Could not load this URL. Use a direct public Supabase{' '}
                      <code className="text-[11px]">/object/public/…</code> link to a .jpg or
                      .png file.
                    </p>
                  ) : (
                    <img
                      src={resolvedHeroImageUrl}
                      alt=""
                      className="max-h-40 w-full object-contain rounded-md bg-black"
                      onLoad={() => setHeroImageError(false)}
                      onError={() => setHeroImageError(true)}
                    />
                  )}
                </div>
              ) : heroImageUrl.trim() ? (
                <p className="text-xs text-destructive">
                  URL is empty after resolving placeholders — check{' '}
                  <code className="text-[11px]">{'{{programImageUrl}}'}</code> or paste a full
                  https link.
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 p-4 border rounded-lg">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Video className="h-4 w-4" />
          Videos
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="https://vimeo.com/123456789"
            value={videoInput}
            onChange={(e) => setVideoInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addVideo();
              }
            }}
          />
          <Button type="button" variant="outline" size="sm" onClick={addVideo}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {videos.length > 0 && (
          <div className="space-y-2">
            {videos.map((url) => (
              <div key={url} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate text-muted-foreground">{url}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onVideosChange(videos.filter((v) => v !== url))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { buildHeroObject, interpolateTemplateText, resolveHeroImageUrl };
